import { kGcsOrgHiddenFolder, kGcsUserFileUploadsBucketRef } from '@/constants/constants-gcs'
import { kDefaultLogoPath, kOrganizationLogoFileName } from '@/constants/constants-ui'
import { deleteObject, getBlob, ref, uploadBytesResumable } from 'firebase/storage'
import { getAppStorage } from '@/firebase/storage-routing'
import { getAuth } from 'firebase/auth'
import { FirebaseError } from 'firebase/app'
import * as Sentry from '@sentry/browser'

function getOrgLogoStorageRef(orgId: string) {
  const storage = getAppStorage({ bucketUrl: kGcsUserFileUploadsBucketRef, legacy_always_cloud: true })
  return ref(storage, `tenants/${orgId}/${kGcsOrgHiddenFolder}/${kOrganizationLogoFileName}`)
}

/**
 * Fetch Org Logo As URL
 * Fetches the organization logo. The three outcomes are distinct on purpose so callers can tell a
 * genuine "no logo" apart from a temporary failure:
 * - Custom logo present: mints an object URL for the fetched bytes.
 * - No logo (no orgId, or GCS `object-not-found`): returns the default Paxton logo path.
 * - Transient / unexpected fetch failure: returns `null` (and reports to Sentry). A caller can then
 *   leave the current logo in place — e.g. a post-upload refresh must not read a flaky fetch as a
 *   deletion and swap the header back to the default.
 * @param orgId the legacy compatible organization id (where we look in GCS for the file)
 * @returns the logo/default path, or `null` on a transient fetch error
 */
export async function fetchOrgLogoAsUrl(orgId: string | null): Promise<string | null> {
  if (!orgId) {
    return kDefaultLogoPath
  }

  const logoStorageRef = getOrgLogoStorageRef(orgId)

  // Fetch the file bytes
  try {
    const blob = await getBlob(logoStorageRef)
    const logoDataUrl = URL.createObjectURL(blob)
    return logoDataUrl
  } catch (error) {
    // A missing object is the normal "org has no logo" case → show the default placeholder.
    if (error instanceof FirebaseError && error.code === 'storage/object-not-found') {
      return kDefaultLogoPath
    }
    // Anything else is transient/unexpected. Report it and signal "unknown" so refresh callers
    // don't mistake a temporary failure for a deletion.
    Sentry.captureException(error)
    return null
  }
}

/**
 * Delete Org Logo
 * Deletes the organization logo file from GCS
 * @param orgId
 * @returns
 */
export async function deleteOrgLogo(orgId: string | null): Promise<void> {
  if (!orgId) {
    return
  }

  const logoStorageRef = getOrgLogoStorageRef(orgId)

  // Delete the file
  await deleteObject(logoStorageRef)

  return
}

type UploadOrgLogoArgs = {
  orgId: string | null | undefined
  imageBlob: Blob
  onProgress: (progress: number) => void
  onError: (error: Error) => void
  onComplete: () => void
}

export function uploadOrgLogo({ orgId, imageBlob, onProgress, onError, onComplete }: UploadOrgLogoArgs) {
  if (!getAuth().currentUser?.uid) {
    throw new Error('Cannot upload an organization logo without an authenticated user.')
  }
  if (!orgId) {
    throw new Error('Cannot upload an organization logo without an organization id.')
  }

  const uploadTask = uploadBytesResumable(getOrgLogoStorageRef(orgId), imageBlob)
  uploadTask.on(
    'state_changed',
    (snapshot) => onProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
    (error) => {
      onError(error)
      Sentry.captureException(error)
    },
    onComplete
  )
}
